// Internal-only access gate. Rendered around the whole app when
// VITE_INTERNAL_ONLY === '1' (dev/stage). Restricts the SPA to seeded internal
// users. OTP-only (no password): enter your email, then a one-time code emailed
// to you. Once a device is verified it is trusted, so future logins on that
// browser/machine skip the OTP step.
//
// Unrecognized email → a "Request access" form (name + phone) that emails the
// platform admin. The admin signs in and, at ?internal=requests, reviews the
// queued requests and Approves/Rejects them. Self-contained (no app contexts).
import { useCallback, useEffect, useState } from 'react'

// Deployed builds call VITE_API_URL/api; local dev uses relative URLs.
// Domain origin only (strip any path VITE_API_URL may include, e.g. /api/v1),
// then append the app's actual route prefix below. This keeps the gate correct
// whether VITE_API_URL is "https://dev-api.x.com" or "https://dev-api.x.com/api".
let API_ORIGIN = ''
try {
  if (import.meta.env.PROD && import.meta.env.VITE_API_URL) {
    API_ORIGIN = new URL(import.meta.env.VITE_API_URL).origin
  }
} catch { API_ORIGIN = '' }
const API_PREFIX = '/api' // installer rewrites this to the app's actual route prefix
const API_BASE = `${API_ORIGIN}${API_PREFIX}`

const TOKEN_KEY = 'internal_access_token'
const DEVICE_ID_KEY = 'internal_access_device_id'
const ACCENT = '#C9A84C'

// Admin review page is requested via ?internal=requests (link in the email).
const REVIEW_MODE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('internal') === 'requests'

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) { id = uuid(); localStorage.setItem(DEVICE_ID_KEY, id) }
  return id
}

async function sha256Hex(str) {
  const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

function fingerprintSource() {
  const n = window.navigator
  return [
    n.userAgent, n.language, n.platform,
    `${window.screen?.width}x${window.screen?.height}x${window.screen?.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|')
}

function deviceLabel() {
  const ua = window.navigator.userAgent
  const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome'
    : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Browser'
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux'
    : /iPhone|iPad/.test(ua) ? 'iOS' : 'Unknown OS'
  return `${browser} on ${os}`
}

async function call(path, { method = 'POST', body, token } = {}) {
  const res = await fetch(`${API_BASE}/internal-access/${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  let data = {}
  try { data = await res.json() } catch { /* ignore */ }
  return { ok: res.ok, status: res.status, data }
}

const post = (path, body, token) => call(path, { method: 'POST', body, token })

export default function InternalGate({ children }) {
  // Self-disabling: outside dev/stage (flag != '1') this is a passthrough, so
  // it can be wired into every app's entry unconditionally. The flag is inlined
  // at build time, so this branch is constant per build (hooks stay consistent).
  if (import.meta.env.VITE_INTERNAL_ONLY !== '1') return children

  const [phase, setPhase] = useState('checking') // checking | email | otp | request | requested | authed
  const [deviceHash, setDeviceHash] = useState(null)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      const dh = await sha256Hex(`${getDeviceId()}|${fingerprintSource()}`)
      if (!alive) return
      setDeviceHash(dh)
      const token = localStorage.getItem(TOKEN_KEY)
      if (!token) { setPhase('email'); return }
      const { ok, data } = await post('session', { device_hash: dh }, token)
      if (!alive) return
      if (ok && data.valid) { setIsAdmin(!!data.user?.is_admin); setPhase('authed') }
      else { localStorage.removeItem(TOKEN_KEY); setPhase('email') }
    })()
    return () => { alive = false }
  }, [])

  const requestCode = useCallback(async () => {
    return post('request-otp', {
      email: email.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
  }, [email, deviceHash])

  const submitEmail = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError(''); setNotice('')
    const { ok, data } = await requestCode()
    setBusy(false)
    if (!ok) {
      if (data.can_request) { setPhase('request'); return }
      // Already applied and awaiting approval (or still to confirm their email):
      // tell them where they stand instead of pushing them back to the form.
      if (data.pending) { setNotice(data.message || 'Your request is awaiting approval.'); return }
      setError(data.message || 'Could not sign in.'); return
    }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice(`We emailed a 6-digit code to ${data.email || email}.`)
    setPhase('otp')
  }, [requestCode, email])

  const submitOtp = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    const { ok, data } = await post('verify-otp', {
      email: email.trim(), code: code.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
    setBusy(false)
    if (!ok) { setError(data.message || 'Invalid code.'); return }
    localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed')
  }, [email, code, deviceHash])

  const resend = useCallback(async () => {
    setBusy(true); setError(''); setNotice('')
    const { ok, data } = await requestCode()
    setBusy(false)
    if (!ok) { setError(data.message || 'Could not resend code.'); return }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice('A new code is on its way.')
  }, [requestCode])

  const submitRequest = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    const { ok, data } = await post('request-access', {
      email: email.trim(), first_name: first.trim(), last_name: last.trim(), phone: phone.trim(), whatsapp: whatsapp.trim(),
    })
    setBusy(false)
    if (!ok) { setError(data.message || 'Could not submit your request.'); return }
    if (data.status === 'already_authorized') {
      setNotice('You already have access — sign in with your email.'); setPhase('email'); return
    }
    setPhase('requested')
  }, [email, first, last, phone])

  if (phase === 'authed') {
    if (REVIEW_MODE && isAdmin) return <ReviewPage token={localStorage.getItem(TOKEN_KEY)} />
    return children
  }

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'App'}</span>
          <span style={S.internalTag}>Internal access</span>
        </div>

        {phase === 'checking' && <div style={S.checking}>Checking your session…</div>}

        {phase === 'email' && (
          <form onSubmit={submitEmail}>
            <h1 style={S.title}>Sign in to continue</h1>
            <p style={S.sub}>This environment is restricted to the internal team. Enter your email and we’ll send you a one-time code.</p>
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" autoFocus required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Please wait…' : 'Send code'}
            </button>
          </form>
        )}

        {phase === 'otp' && (
          <form onSubmit={submitOtp}>
            <h1 style={S.title}>Enter your code</h1>
            {notice && <p style={S.sub}>{notice}</p>}
            <label style={S.label}>6-digit code</label>
            <input style={{ ...S.input, letterSpacing: '0.4em', textAlign: 'center', fontSize: 20 }}
              inputMode="numeric" autoFocus required value={code} maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={resend} disabled={busy}>Resend code</button>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setCode(''); setError('') }}>
                Use a different email
              </button>
            </div>
          </form>
        )}

        {phase === 'request' && (
          <form onSubmit={submitRequest}>
            <h1 style={S.title}>Request access</h1>
            <p style={S.sub}>Your email isn’t on the internal list yet. Send a request and the platform admin will review it.</p>
            <label style={S.label}>Email address</label>
            <input style={{ ...S.input, opacity: 0.65, cursor: 'not-allowed' }} type="email" value={email} disabled readOnly />
            <div style={S.grid2}>
              <div>
                <label style={S.label}>First name</label>
                <input style={S.input} autoFocus required value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First" />
              </div>
              <div>
                <label style={S.label}>Last name</label>
                <input style={S.input} required value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last" />
              </div>
            </div>
            <label style={S.label}>Contact phone number</label>
            <input style={S.input} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" />
            <label style={S.label}>WhatsApp number</label>
            <input style={S.input} type="tel" required value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+1 555 123 4567" />
            <div style={S.hint || { fontSize: 12, opacity: 0.7, marginTop: 4 }}>Include your country code, starting with +. This is how we'll reach you.</div>
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Sending…' : 'Request access'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setError('') }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {phase === 'requested' && (
          <div>
            <h1 style={S.title}>Request sent — check your email</h1>
            <p style={S.sub}>Thanks. We’ve emailed you a link to <strong>confirm your email address</strong> — please open it and press “Confirm my email” so we know you’re reachable. We’ll then review your request, and once it’s approved you’ll be able to sign in with a one-time code.</p>
            <button style={S.btn} type="button" onClick={() => { setPhase('email'); setFirst(''); setLast(''); setPhone(''); setError('') }}>
              Done
            </button>
          </div>
        )}

        <div style={S.foot}>Trusted devices skip the code next time · A Stacza product</div>
      </div>
    </div>
  )
}

// ── Admin review page (?internal=requests) ─────────────────────────────────
function ReviewPage({ token }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [actingId, setActingId] = useState(null)

  const load = useCallback(async () => {
    const { ok, data } = await call('requests', { method: 'GET', token })
    if (!ok) { setError(data.message || 'Could not load requests.'); setRows([]); return }
    setRows(data.data || [])
  }, [token])

  useEffect(() => { load() }, [load])

  const act = useCallback(async (id, action) => {
    setActingId(id); setError('')
    const { ok, data } = await post(`requests/${id}/${action}`, {}, token)
    setActingId(null)
    if (!ok) { setError(data.message || `Could not ${action}.`); return }
    load()
  }, [token, load])

  const fmtDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
  }

  return (
    <div style={{ ...S.wrap, alignItems: 'flex-start', padding: '40px 20px' }}>
      <div style={{ ...S.card, maxWidth: 860 }}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'App'}</span>
          <span style={S.internalTag}>Access requests</span>
        </div>
        <h1 style={S.title}>Access requests</h1>
        <p style={S.sub}>Newest first. Approving grants access in this environment and adds the email to the seeder so it promotes to the others.</p>
        {error && <div style={S.error}>{error}</div>}

        {rows === null && <div style={S.checking}>Loading…</div>}
        {rows !== null && rows.length === 0 && <div style={S.checking}>No access requests yet.</div>}

        {rows !== null && rows.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Requester</th>
                  <th style={S.th}>Phone</th>
                  <th style={S.th}>Requested</th>
                  <th style={S.th}>Status</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{`${r.first_name} ${r.last_name}`.trim() || '—'}</div>
                      <div style={{ fontSize: 12, color: 'rgba(243,241,234,0.55)' }}>{r.email}</div>
                    </td>
                    <td style={S.td}>{r.phone || '—'}</td>
                    <td style={S.td}>{fmtDate(r.created_at)}</td>
                    <td style={S.td}><StatusBadge status={r.status} /></td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.status === 'pending' ? (
                        <>
                          <button style={S.approveBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'approve')}>Approve</button>
                          <button style={S.rejectBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'reject')}>Reject</button>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: 'rgba(243,241,234,0.5)' }}>
                          {r.reviewed_by ? `by ${r.reviewed_by}` : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={S.foot}>
          <a href={window.location.pathname} style={{ color: 'rgba(243,241,234,0.5)' }}>← Back to app</a>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: 'rgba(201,168,76,0.15)', bd: 'rgba(201,168,76,0.4)', c: '#e7c667', t: 'Pending' },
    approved: { bg: 'rgba(76,201,124,0.14)', bd: 'rgba(76,201,124,0.4)', c: '#6ddf9a', t: 'Approved' },
    rejected: { bg: 'rgba(255,80,80,0.12)',  bd: 'rgba(255,80,80,0.35)', c: '#ff9a9a', t: 'Rejected' },
  }
  const s = map[status] || map.pending
  return (
    <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.c }}>{s.t}</span>
  )
}

const S = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    background: 'radial-gradient(circle at top, #16131c 0%, #0b0b12 60%, #060608 100%)',
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", color: '#f3f1ea' },
  card: { width: '100%', maxWidth: 400, background: 'rgba(255,255,255,0.035)',
    border: '1px solid rgba(255,255,255,0.09)', borderRadius: 18, padding: '32px 28px',
    boxShadow: '0 24px 70px rgba(0,0,0,0.45)' },
  brandRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 },
  badge: { fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em' },
  internalTag: { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: ACCENT,
    border: `1px solid ${ACCENT}55`, background: `${ACCENT}14`, padding: '4px 9px', borderRadius: 999 },
  checking: { padding: '28px 0', textAlign: 'center', color: 'rgba(243,241,234,0.6)', fontSize: 14 },
  title: { fontSize: 22, fontWeight: 700, margin: '0 0 6px' },
  sub: { fontSize: 13.5, color: 'rgba(243,241,234,0.6)', margin: '0 0 22px', lineHeight: 1.5 },
  label: { display: 'block', fontSize: 12, color: 'rgba(243,241,234,0.65)', margin: '14px 0 6px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', color: '#fff',
    fontSize: 14, outline: 'none' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  btn: { width: '100%', marginTop: 22, padding: '13px 18px', borderRadius: 10, border: 'none',
    background: `linear-gradient(135deg, ${ACCENT}, #b08f3c)`, color: '#1a1505', fontWeight: 700,
    fontSize: 14.5, cursor: 'pointer' },
  row: { display: 'flex', justifyContent: 'space-between', marginTop: 16 },
  linkBtn: { background: 'none', border: 'none', color: 'rgba(243,241,234,0.7)', fontSize: 12.5,
    cursor: 'pointer', textDecoration: 'underline', padding: 0 },
  error: { marginTop: 14, fontSize: 13, color: '#ff8f8f', background: 'rgba(255,80,80,0.08)',
    border: '1px solid rgba(255,80,80,0.25)', borderRadius: 8, padding: '9px 12px' },
  foot: { marginTop: 26, fontSize: 11, color: 'rgba(243,241,234,0.35)', textAlign: 'center' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11.5, textTransform: 'uppercase',
    letterSpacing: 0.6, color: 'rgba(243,241,234,0.45)', borderBottom: '1px solid rgba(255,255,255,0.09)' },
  td: { padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'top' },
  approveBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 8, border: 'none', cursor: 'pointer',
    background: 'linear-gradient(135deg,#4cc97c,#2fa661)', color: '#06210f', fontWeight: 700, fontSize: 12.5 },
  rejectBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 8, cursor: 'pointer',
    background: 'transparent', border: '1px solid rgba(255,120,120,0.4)', color: '#ff9a9a', fontWeight: 600, fontSize: 12.5 },
}
